import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from 'react-query';
import { searchUserByEmail, resetPasswordWithEmailAndPhone } from '../services/userApi';
import { useTranslation } from 'react-i18next';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import LanguageSelector from '../components/LanguageSelector';
import 'react-phone-number-input/style.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code and new password
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    const [isChecking, setIsChecking] = useState(false);
    
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const { mutate: resetPasswordMutation, isLoading: isUpdatingPassword } = useMutation(
        resetPasswordWithEmailAndPhone, {
        onSuccess: () => {
            setSuccessMessage(t('forgotPassword.resetSuccess', 'Your password has been successfully reset. You can now log in with your new password.'));
            setError('');
            setTimeout(() => navigate('/login'), 3000);
        },
        onError: (err) => {
            setError(err.message || t('forgotPassword.resetFailed', 'Failed to reset password. Please try again.'));
            setSuccessMessage('');
        }
    });

    const handleVerifyUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!email || !phone) {
            setError(t('forgotPassword.emailAndPhoneRequired', 'Please provide both email and phone number.'));
            return;
        }
        if (!EMAIL_REGEX.test(email)) {
            setError(t('forgotPassword.emailInvalid', 'Please enter a valid email address.'));
            return;
        }
        if (!isValidPhoneNumber(phone)) {
            setError(t('forgotPassword.phoneInvalid', 'Please enter a valid phone number.'));
            return;
        }

        setIsChecking(true);
        try {
            const users = await searchUserByEmail(email);
            if (users && users.length > 0) {
                const user = users[0];
                if (user.phone === phone) {
                    setStep(2); // Proceed to the password reset step
                    setError('');
                } else {
                    setError(t('forgotPassword.phoneMismatch', 'The phone number does not match the account associated with this email.'));
                }
            } else {
                setError(t('forgotPassword.noAccount', 'No account found with this email address.'));
            }
        } catch (err) {
            setError(t('forgotPassword.verifyFailed', 'Failed to verify your details. Please try again.'));
        } finally {
            setIsChecking(false);
        }
    };

    const handleResetPassword = (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError(t('forgotPassword.passwordMismatch', 'Passwords do not match.'));
            return;
        }
        if (!newPassword) {
            setError(t('forgotPassword.newPasswordRequired', 'Please enter a new password.'));
            return;
        }
        setError('');
        resetPasswordMutation({ email, phone, newPassword });
    };

    return (
        <div className="app-container" style={{ background: 'linear-gradient(180deg, #0b3a7a 0%, #0b234a 100%)', minHeight: '100vh' }}>
            <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
              <LanguageSelector />
            </div>
            <div style={{ padding: '2rem' }}>
                <div className="content-card" style={{ maxWidth: '400px', margin: '0 auto' }}>
                    <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
                        {t('forgotPassword.title', 'Forgot Password')}
                    </h2>
                    
                    {error && <p className="form-error" style={{ textAlign: 'center', marginBottom: '1rem' }}>{error}</p>}
                    {successMessage && <p style={{ color: 'green', textAlign: 'center', marginBottom: '1rem' }}>{successMessage}</p>}

                    {step === 1 && (
                        <form onSubmit={handleVerifyUser}>
                            <p style={{ textAlign: 'center', marginBottom: '2rem', color: '#666' }}>
                                {t('forgotPassword.enterDetailsPrompt', 'Enter your registered email and phone number to reset your password.')}
                            </p>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    placeholder={t('login.emailPlaceholder', 'Email ID')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label className="form-label">Phone Number</label>
                                <PhoneInput
                                    placeholder={t('form.contact', 'Enter phone number')}
                                    value={phone}
                                    onChange={setPhone}
                                    numberInputProps={{ className: 'form-input' }}
                                    defaultCountry="IN"
                                />
                            </div>
                            <button type="submit" className="btn-primary" disabled={isChecking} style={{ width: '100%' }}>
                                {isChecking ? t('common.loading', 'Verifying...') : t('forgotPassword.verifyUser', 'Verify User')}
                            </button>
                        </form>
                    )}

                    {step === 2 && !successMessage.includes('successfully reset') && (
                        <form onSubmit={handleResetPassword}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label className="form-label">{t('forgotPassword.newPassword', 'New Password')}</label>
                                <input type="password" className="form-input" placeholder={t('forgotPassword.newPasswordPlaceholder', 'Enter new password')} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                            </div>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label className="form-label">{t('forgotPassword.confirmPassword', 'Confirm New Password')}</label>
                                <input type="password" className="form-input" placeholder={t('forgotPassword.confirmPasswordPlaceholder', 'Confirm new password')} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                            </div>
                            <button type="submit" className="btn-primary" disabled={isUpdatingPassword} style={{ width: '100%' }}>
                                {isUpdatingPassword ? t('common.loading', 'Loading...') : t('forgotPassword.resetPassword', 'Reset Password')}
                            </button>
                        </form>
                    )}

                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                        <Link to="/login" style={{ color: '#0b3a7a', textDecoration: 'none' }}>
                            {t('forgotPassword.backToLogin', 'Back to Login')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;